import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Check, Clock3, Sun, Users } from "lucide-react";
import { ACTIVITIES, CATEGORIES } from "../../data";

export default function IslandExplorer({ saved, toggle }) {
    const [cat, setCat] = useState("all");
    const list = cat === "all" ? ACTIVITIES : ACTIVITIES.filter((a) => a.cat === cat);
    const isSaved = (id) => saved.some((s) => s.id === id);

    return (
        <div data-testid="island-explorer-section">
            <div className="mb-8">
                <p className="text-xs uppercase tracking-[0.3em] text-[#C49A45] font-semibold">Step two</p>
                <h3 className="font-serif-d font-light text-3xl sm:text-4xl lg:text-5xl text-[#0F2837] mt-2">
                    Explore outside, <em className="text-[#2D6A76]">choose your moments.</em>
                </h3>
                <p className="mt-4 text-[#5C6A78] text-sm max-w-2xl">Seven island categories, two-plus real experiences in each. Tap a photograph to add it to your island day — remove it the same way.</p>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-3 -mx-5 px-5 lg:mx-0 lg:px-0 lg:flex-wrap" data-testid="category-filters">
                <button onClick={() => setCat("all")} data-testid="cat-filter-all"
                    className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-semibold border transition-all ${cat === "all" ? "bg-[#0F2837] text-white border-[#0F2837]" : "border-[#E5DCCB] bg-white text-[#5C6A78] hover:border-[#C49A45]"}`}>
                    All
                </button>
                {CATEGORIES.map((c) => (
                    <button key={c.id} onClick={() => setCat(c.id)} data-testid={`cat-filter-${c.id}`}
                        className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-semibold border transition-all flex items-center gap-2 ${cat === c.id ? "bg-[#0F2837] text-white border-[#0F2837]" : "border-[#E5DCCB] bg-white text-[#5C6A78] hover:border-[#C49A45]"}`}>
                        <span className="text-[#C49A45]">{c.symbol}</span> {c.label}
                    </button>
                ))}
            </div>

            <motion.div layout className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="activity-grid">
                <AnimatePresence mode="popLayout">
                    {list.map((a) => (
                        <motion.article key={a.id} layout
                            initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.94 }}
                            transition={{ duration: 0.35 }}
                            className={`exp-card rounded-3xl overflow-hidden bg-white border ${isSaved(a.id) ? "border-[#C49A45] ring-2 ring-[#C49A45]/40" : "border-[#E5DCCB]"}`}
                            data-testid={`activity-card-${a.id}`}>
                            <button onClick={() => toggle(a)} className="relative block w-full h-48 overflow-hidden text-left" data-testid={`activity-photo-${a.id}`}>
                                <img src={a.img} alt={a.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
                                <span className="absolute top-3 left-3 rounded-full bg-[#0F2837]/80 backdrop-blur text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 flex items-center gap-1.5">
                                    <Clock3 size={11} className="text-[#C49A45]" /> {a.time}
                                </span>
                                <span className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur transition-all ${isSaved(a.id) ? "bg-[#C49A45] text-white" : "bg-white/85 text-[#0F2837]"}`}
                                    data-testid={`experience-card-add-button-${a.id}`}>
                                    {isSaved(a.id) ? <Check size={16} /> : <Plus size={16} />}
                                </span>
                                {isSaved(a.id) && <span className="absolute bottom-3 left-3 rounded-full bg-[#C49A45] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1">In your day</span>}
                            </button>
                            <div className="p-5">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    {a.tags.map((t) => (
                                        <span key={t} className="rounded-full bg-[#F3EDE2] text-[#2D6A76] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 flex items-center gap-1"><Users size={9} /> {t}</span>
                                    ))}
                                    <span className="rounded-full bg-[#F3EDE2] text-[#C49A45] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 flex items-center gap-1"><Sun size={9} /> {a.best}</span>
                                </div>
                                <h4 className="font-serif-d text-xl text-[#0F2837] mt-3 leading-snug">{a.title}</h4>
                                <p className="text-[13px] text-[#5C6A78] mt-1.5 leading-relaxed">{a.desc}</p>
                            </div>
                        </motion.article>
                    ))}
                </AnimatePresence>
            </motion.div>
        </div>
    );
}
